// activePropertySlides
import _ from 'lodash'
import {
  doProductTypesMatch,
  getCanonicalProductType,
  getDwellingTypeFromDwelling,
  getPropertyTypeFromProperty,
  isProductCarouselPackagesRoute,
  isProductCarouselTurnkeysRoute,
  isProductRoute
} from '@/utils/livingRoute'

const isAvailableProperty = (property) => {
  return (property.status || '').toString().toLowerCase() === 'available'
}

const getAvailableProductCarouselProperties = (getters) => {
  return getters['property/properties'].filter(property => {
    return !property.isDisplayHome && isAvailableProperty(property)
  })
}

const getPropertyImages = (property, rootState) => {
  if (!property.dwelling) {
    return [...(property.viewImages || []), ...(property.galleryImages || [])]
  }

  const dwelling = property.dwelling
  const floorplanImages = dwelling.floorplanImages[rootState.language.code] || dwelling.floorplanImages.en || []

  return [
    ...(dwelling.facadeImages || []),
    ...(dwelling.galleryImages || []),
    ...floorplanImages,
    ...(property.viewImages || []),
    ...(property.galleryImages || []),
  ]
}

const mapPropertyToCarouselSlide = (property, rootState) => {
  const dwelling = property.dwelling
  const displayLabel = property.displayLabel ? property.displayLabel[rootState.language.code] || property.displayLabel.en : ''
  const images = getPropertyImages(property, rootState)

  return {
    backgroundImage: images[0],
    baths: dwelling ? dwelling.baths : '',
    beds: dwelling ? dwelling.beds : '',
    builder: dwelling ? dwelling.builderLabel[rootState.language.code] || dwelling.builderLabel['en'] : '',
    colourScheme: dwelling ? dwelling.colourScheme[rootState.language.code] || dwelling.colourScheme['en'] : '',
    houseDesign: dwelling ? dwelling.houseDesign[rootState.language.code] || dwelling.houseDesign['en'] : '',
    cars: dwelling ? dwelling.cars : '',
    _id: property._id,
    images: images,
    lotNumber: displayLabel || property.label,
    lotSize: property.size,
    dwellingSize: dwelling ? dwelling.size : '',
    name: dwelling ? dwelling.displayName[rootState.language.code] || dwelling.displayName['en'] || dwelling.label : '',
    price: property.price,
    virtualTourUrl: dwelling ? dwelling.virtualTourUrl : false,
    dwellingOverlayImage: dwelling ? dwelling.overlayImage : null,
    type: dwelling ? 'turnkey' : 'land',
    frontage: property.frontage,
    depth: property.depth,
    viewLabel: property.viewLabel
  }
}

export default function (state, getters, rootState) {
  const routeName = rootState.route.name

  if (['carousel'].includes(routeName)) {
    if (!isProductRoute(rootState.route)) return []

    let properties = getAvailableProductCarouselProperties(getters)

    if (isProductCarouselTurnkeysRoute(rootState.route)) {
      properties = properties.filter(property => !!property.dwelling)
    }

    if (rootState.route.params.productType) {
      const productType = getCanonicalProductType(rootState.route.params.productType)
      properties = properties.filter(property => doProductTypesMatch(getPropertyTypeFromProperty(property), productType))
    }

    properties = _.sortBy(properties, property => property.price || 0)

    return properties.map(property => mapPropertyToCarouselSlide(property, rootState))
  }

  if (['packages', 'package-modal'].includes(routeName)) {
    let _packages = getters['property/livingOptionsPackages'].filter(_package => {
      return !_package.filter
    })  

    // filter out packages that dont have a property or dwelling
    _packages = _packages.filter(_package => {
      return _package.property && _package.dwelling
    })

    if (rootState.route.query.lotId) {
      _packages = _packages.filter(_package => {
        return _package.property._id === rootState.route.query.lotId
      })
    }

    if (isProductRoute(rootState.route) && isProductCarouselPackagesRoute(rootState.route) && rootState.route.params.packageType) {
      const packageType = getCanonicalProductType(rootState.route.params.packageType)
      _packages = _packages.filter(_package => {
        return _package.dwelling && doProductTypesMatch(getDwellingTypeFromDwelling(_package.dwelling), packageType)
      })
    }

    _packages = _.sortBy(_packages, 'price')

    
    return _packages.map(_package => {
      const displayLabel = _package.displayLabel ? _package.displayLabel[rootState.language.code] || _package.displayLabel['en'] : ''
      return {
        backgroundImage: [..._package.dwelling.facadeImages, ..._package.dwelling.galleryImages, ..._package.property.viewImages, ..._package.property.galleryImages, ..._package.dwelling.floorplanImages[rootState.language.code]][0],
        baths: _package.dwelling ? _package.dwelling.baths : '',
        beds: _package.dwelling ? _package.dwelling.beds : '',
        builder: _package.dwelling ? _package.dwelling.builderLabel[rootState.language.code] || _package.dwelling.builderLabel['en']: '',
        colourScheme: _package.dwelling ? _package.dwelling.colourScheme[rootState.language.code] || _package.dwelling.colourScheme['en'] : '',
        houseDesign: _package.dwelling ? _package.dwelling.houseDesign[rootState.language.code] || _package.dwelling.houseDesign['en'] : '',
        cars: _package.dwelling ? _package.dwelling.cars : '',
        _id: _package.id,
        images: [..._package.dwelling.facadeImages, ..._package.dwelling.galleryImages, ..._package.property.viewImages, ..._package.property.galleryImages, ..._package.dwelling.floorplanImages[rootState.language.code]], 
        lotNumber: _package.property.label,
        lotSize: _package.property.size,
        dwellingSize: _package.dwelling.size,
        name: displayLabel || _package.label,
        price: _package.price,
        priceLabel: _package.priceLabel[rootState.language.code],
        virtualTourUrl: _package.dwelling.virtualTourUrl,
        packageOverlayImage: _package.overlayImage,
        dwellingOverlayImage: _package.dwelling.overlayImage,
        type: 'package'
      }
    })
  }

  if (['dwellings', 'dwelling-modal'].includes(routeName)) {
    // const dwellingTypes = rootGetters.newConfig.dwellingPage.types
    let dwellings = getters['property/livingOptionsDwellings']

    if (rootState.route.query.lotId) {
      dwellings = dwellings.filter(dwelling => {
        return dwelling.suitablePropertyIds.includes(rootState.route.query.lotId)
      })
    }

    if (rootState.route.query.dwellingType) {
      dwellings = dwellings.filter(dwelling => {
        return dwelling.type === rootState.route.query.dwellingType
      })
    }

    if (isProductRoute(rootState.route) && rootState.route.params.dwellingType) {
      const dwellingType = getCanonicalProductType(rootState.route.params.dwellingType)
      dwellings = dwellings.filter(dwelling => {
        return doProductTypesMatch(getDwellingTypeFromDwelling(dwelling), dwellingType)
      })
    }


    dwellings = _.sortBy(dwellings, 'price')
    
    return dwellings
      .map(dwelling => {
        const fpImages = dwelling.floorplanImages[state.language.code] || dwelling.floorplanImages['en']
        return {
          backgroundImage: [...dwelling.facadeImages, ...dwelling.galleryImages, ...fpImages][0],
          baths: dwelling ? dwelling.baths : '',
          beds: dwelling ? dwelling.beds : '',
          builder: dwelling ? dwelling.builderLabel[rootState.language.code] || dwelling.builderLabel['en'] : '',
          colourScheme: dwelling ? dwelling.colourScheme[rootState.language.code] || dwelling.colourScheme['en'] : '',
          houseDesign: dwelling ? dwelling.houseDesign[rootState.language.code] || dwelling.houseDesign['en'] : '',
          cars: dwelling ? dwelling.cars : '',
          _id: dwelling._id,
          images: [...dwelling.facadeImages, ...dwelling.galleryImages, ...fpImages], 
          lotNumber: null,
          dwellingSize: dwelling.size,
          name: dwelling.displayName[rootState.language.code] || dwelling.displayName['en'],
          price: dwelling.price,
          virtualTourUrl: dwelling.virtualTourUrl,
          minimumFrontage: dwelling.fitsOnProperty,
          dwellingOverlayImage: dwelling.overlayImage,
          type: 'dwelling',
         
        }
    })
  }

  if (['favourites', 'favourite-modal'].includes(routeName)) {
    const favourites = getters['favourites/favourites']

    return favourites
      .map(favourite => {
        let type = ''
        if (favourite.propertyId && favourite.dwellingId) {
          type = 'package'
        } else if (favourite.dwellingId) {
          type = 'turnkey'
        } else if (favourite.type && (favourite.type.toLowerCase() === 'house' || favourite.type.toLowerCase() === 'townhouse' || favourite.type.toLowerCase() === 'apartment')) {
          type = 'house'
        } else {
          type = 'land'
        }

        let backgroundImage = []
        let images = []
        if (type === 'package') {
          const fpImages = favourite.dwelling.floorplanImages[rootState.language.code] || favourite.dwelling.floorplanImages.en
          backgroundImage = [...favourite.dwelling.facadeImages, ...favourite.dwelling.galleryImages, ...favourite.property.viewImages, ...favourite.property.galleryImages, ...fpImages][0];
          images = [...favourite.dwelling.facadeImages, ...favourite.dwelling.galleryImages, ...favourite.property.viewImages, ...favourite.property.galleryImages, ...fpImages];
        }
        if (type === 'house') {
          const fpImages = favourite.floorplanImages[rootState.language.code] || favourite.floorplanImages.en
          backgroundImage = [...favourite.galleryImages, ...fpImages][0]
          images = [...favourite.galleryImages, ...fpImages]
        }
        if (type === 'turnkey') {
          const fpImages = favourite.dwelling.floorplanImages[rootState.language.code] || favourite.dwelling.floorplanImages.en
          backgroundImage = [...favourite.dwelling.facadeImages, ...favourite.dwelling.galleryImages][0]
          images = [...favourite.dwelling.facadeImages, ...favourite.dwelling.galleryImages, ...fpImages]
        }
        if (type === 'land') {
          backgroundImage = [...favourite.viewImages, ...favourite.galleryImages][0]
          images = [...favourite.viewImages, ...favourite.galleryImages]
        }

        let displayLabel = favourite.displayLabel ? favourite.displayLabel[rootState.language.code] || favourite.displayLabel['en'] : ''

        let dwellingLabel = type === 'turnkey' ? (favourite.dwelling.displayName[rootState.language.code] || favourite.dwelling.displayName['en']) : false

        return {
          backgroundImage: backgroundImage,
          baths: favourite.dwelling ? favourite.dwelling.baths : type === 'house' ? favourite.baths : '',
          beds: favourite.dwelling ? favourite.dwelling.beds : type === 'house' ? favourite.beds : '',
          builder: favourite.dwelling ?
            favourite.dwelling.builderLabel[rootState.language.code] || favourite.dwelling.builderLabel['en'] :
            type === 'house' ? favourite.builderLabel[rootState.language.code] || favourite.builderLabel['en'] : '',
          colourScheme: favourite.dwelling ? 
            favourite.dwelling.colourScheme[rootState.language.code] || favourite.dwelling.colourScheme['en'] : 
            type === 'house' ? 
              favourite.colourScheme[rootState.language.code] || favourite.colourScheme['en'] :
              '',
          houseDesign: favourite.dwelling ?
            favourite.dwelling.houseDesign[rootState.language.code] || favourite.dwelling.houseDesign['en'] : 
            type === 'house' ? 
              favourite.houseDesign[rootState.language.code] || favourite.houseDesign['en'] :
              '',
          cars: favourite.dwelling ? favourite.dwelling.cars : type === 'house' ? favourite.cars : '',
          _id: favourite._id,
          images: images,
          lotNumber: type === 'package' ?
            favourite.property.label[rootState.language.code] || displayLabel || favourite.property.label :
            displayLabel || favourite.label,
          lotSize: type === 'package' ? favourite.property.size : favourite.size,
          name: type === 'package' ? dwellingLabel || favourite.label : type === 'turnkey' ? dwellingLabel : '',
          price: favourite.price,
          packageOverlayImage: type === 'package' ? favourite.property.overlayImage : null,
          dwellingOverlayImage: type === 'package' ? favourite.dwelling.overlayImage : null,
          type: type,
          frontage: favourite.frontage,
          depth: favourite.depth,
          viewLabel: favourite.viewLabel
        }
    })
  }
  
  return []
}
